import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquarePlus, X } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/supabase-client'
import { useConsoleCapture } from './useConsoleCapture'
import FeedbackForm from './FeedbackForm'

export default function FeedbackWidget({ user }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contentVisible, setContentVisible] = useState(false)
  const widgetRef = useRef(null)
  const { getRecentLogs } = useConsoleCapture()

  // Delay content fade-in after panel expands
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setContentVisible(true), 150)
      return () => clearTimeout(timer)
    }
    setContentVisible(false)
  }, [isOpen])

  const captureScreenshot = async () => {
    // Hide widget before capture
    if (widgetRef.current) widgetRef.current.style.display = 'none'

    try {
      const { toBlob } = await import('html-to-image')
      const blob = await toBlob(document.body, {
        quality: 0.8,
        pixelRatio: 0.5,
      })

      if (!blob) throw new Error('Screenshot capture returned empty')

      const fileName = `feedback-${Date.now()}.png`
      const { data, error } = await supabase.storage
        .from('feedback-screenshots')
        .upload(fileName, blob, { cacheControl: '3600', upsert: false })

      if (error) throw error

      const { data: urlData } = supabase.storage
        .from('feedback-screenshots')
        .getPublicUrl(data.path)

      return urlData.publicUrl
    } catch (err) {
      console.error('Screenshot capture failed:', err)
      return null
    } finally {
      if (widgetRef.current) widgetRef.current.style.display = ''
    }
  }

  const handleSubmit = async ({ type, description, includeScreenshot, includeConsoleLogs }) => {
    setIsSubmitting(true)

    try {
      let screenshotUrl = null
      let consoleLogs = null

      if (includeScreenshot) {
        screenshotUrl = await captureScreenshot()
      }

      if (includeConsoleLogs) {
        consoleLogs = getRecentLogs()
      }

      const payload = {
        action: 'create',
        type,
        description,
        screenshotUrl,
        consoleLogs,
        context: {
          userEmail: user?.email || 'unknown',
          currentUrl: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
      }

      const { data: rawData, error } = await supabase.functions.invoke('feedback', { body: payload })

      if (error) throw error

      // supabase-js v2 may return parsed JSON or a raw string
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData
      if (!data?.success) throw new Error(data?.error || 'Unknown error')

      toast.success('Feedback submitted — thank you!')
      setIsOpen(false)
    } catch (err) {
      console.error('Feedback submission failed:', err)
      toast.error('Failed to submit feedback. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const widget = (
    <div
      ref={widgetRef}
      className="fixed bottom-6 right-6 z-[45] md:z-[60]"
      style={{ originX: '100%', originY: '100%' }}
    >
      {/* Morphing container — both states rendered, visibility toggled */}
      <div
        className="bg-white border border-border shadow-xl overflow-hidden transition-all duration-300 ease-out"
        style={{
          width: isOpen ? 360 : 130,
          height: isOpen ? 'auto' : 40,
          borderRadius: isOpen ? 12 : 9999,
          maxHeight: isOpen ? 600 : 40,
        }}
      >
        {/* Collapsed button content */}
        <div
          className="absolute inset-0 flex items-center justify-center transition-opacity duration-200"
          style={{
            opacity: isOpen ? 0 : 1,
            pointerEvents: isOpen ? 'none' : 'auto',
          }}
        >
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors whitespace-nowrap"
          >
            <MessageSquarePlus className="h-4 w-4" />
            Feedback
          </button>
        </div>

        {/* Expanded panel content */}
        <div
          className="transition-opacity duration-200"
          style={{
            opacity: contentVisible ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
          }}
        >
          {isOpen && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Send Feedback</h3>
                <button
                  onClick={() => !isSubmitting && setIsOpen(false)}
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  disabled={isSubmitting}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <FeedbackForm
                onSubmit={handleSubmit}
                onCancel={() => setIsOpen(false)}
                isSubmitting={isSubmitting}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(widget, document.body)
}
